import { Components } from "formiojs";

export default class ConnectToIconManager extends Components.components.button {
  static schema(...extend) {
    return super.schema(
      {
        type: "connecttoiconmanager",
        label: "Connect to Icon Manager",
        key: "connecttoiconmanager",
        customClass: "connect-icon-btn",
        apiEndpoint: "",
        targetUrl: "",
        allowedRoles: ["SYSTEM_ADMIN", "COMMUNITY_ADMIN"],
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: "Connect to Icon Manager",
      group: "basic",
      icon: "link",
      weight: 70,
      schema: ConnectToIconManager.schema(),
    };
  }

  static editForm(...extend) {
    const base = Components.components.button.editForm(...extend);
    const displayPanel = base.components?.[0]?.components?.find(
      (panel) => panel.key === "display"
    );

    if (displayPanel && Array.isArray(displayPanel.components)) {
      const labelIndex = displayPanel.components.findIndex(
        (c) => c.key === "label"
      );

      displayPanel.components.splice(
        labelIndex + 1,
        0,
        {
          type: "textfield",
          input: true,
          key: "apiEndpoint",
          label: "Token API Endpoint",
          placeholder: "https://api.example.com/token",
          weight: 10,
          validate: {
            required: true,
          },
        },
        {
          type: "textfield",
          input: true,
          key: "targetUrl",
          label: "Target URL",
          placeholder: "https://icon-manager.example.com",
          weight: 11,
          validate: {
            required: true,
          },
        },
        {
          type: "selectboxes",
          input: true,
          key: "allowedRoles",
          label: "Sichtbar für Rollen",
          tooltip: "Legt fest, welche OZG-Manager-Rollen den Button sehen dürfen.",
          weight: 12,
          defaultValue: {
            SYSTEM_ADMIN: true,
            COMMUNITY_ADMIN: true,
            COMMUNITY_MEMBER: false,
            TESTER: false,
            FORM_BUILDER: false,
          },
          values: [
            {
              label: "System Administrator",
              value: "SYSTEM_ADMIN",
              shortcut: "",
            },
            {
              label: "Gemeinde Administrator",
              value: "COMMUNITY_ADMIN",
              shortcut: "",
            },
            {
              label: "Gemeinde Mitarbeiter",
              value: "COMMUNITY_MEMBER",
              shortcut: "",
            },
            {
              label: "Tester",
              value: "TESTER",
              shortcut: "",
            },
            {
              label: "Formular Ersteller",
              value: "FORM_BUILDER",
              shortcut: "",
            },
          ],
        }
      );
    }

    return base;
  }

  render(children) {
    return super.render(children);
  }

  attach(element) {
    const result = super.attach(element);

    if (this.attachedOnce) return result;
    this.attachedOnce = true;

    const button = element.querySelector(".connect-icon-btn button");

    if (!button) {
      console.error("[ConnectToIconManager] Button not found.");
      return result;
    }

    const isBuilderOrEditor =
      !!this.builderMode ||
      !!this.options?.builder ||
      !!this.root?.builderMode ||
      !!this.root?.options?.builder ||
      !!element.closest(".formio-builder") ||
      !!element.closest(".formio-dialog") ||
      !!element.closest(".formio-component-edit");

    if (!isBuilderOrEditor) {
      const allowedRolesConfig = this.component.allowedRoles || {
        SYSTEM_ADMIN: true,
        COMMUNITY_ADMIN: true,
      };

      const allowedRoles = Array.isArray(allowedRolesConfig)
        ? allowedRolesConfig
        : Object.keys(allowedRolesConfig).filter((role) => allowedRolesConfig[role]);

      const currentUser = window.currentUser || {};
      const userGroups = Array.isArray(currentUser.groups) ? currentUser.groups : [];

      const hasAllowedRole = userGroups.some((role) =>
        allowedRoles.includes(String(role).trim())
      );

      if (!hasAllowedRole) {
        const wrapper =
          button.closest(".formio-component") ||
          button.closest(".connect-icon-btn") ||
          button.parentElement;

        if (wrapper) {
          wrapper.style.display = "none";
        } else {
          button.style.display = "none";
        }

        return result;
      }
    }

    const errorDiv = document.createElement("div");
    errorDiv.className = "text-danger mt-2";
    button.parentElement?.appendChild(errorDiv);

    button.addEventListener("click", async (event) => {
      event.preventDefault();
      event.stopPropagation();

      const apiEndpoint = (this.component.apiEndpoint || "").trim();
      const targetUrl = (this.component.targetUrl || "").trim();
      const originalText = button.innerText;

      if (!apiEndpoint || !targetUrl) {
        errorDiv.innerText = "API-Endpoint oder Target-URL ist nicht konfiguriert.";
        return;
      }

      errorDiv.innerText = "";
      button.disabled = true;
      button.innerText = "Verbinden...";

      try {
        const currentUser = window.currentUser || {};
        const communityData = window.communityData || {};

        const payload = {
          community_id: currentUser.communityId ?? communityData._id ?? null,
          community_number: communityData.ars ?? null,
          community_identifier:
            currentUser.CommunityIdentifier ??
            window.communityIdentifier ??
            communityData.communityIdentifier ??
            null,
          email: currentUser.emailAddress ?? null,
          first_name: currentUser.firstName ?? null,
          last_name: currentUser.lastName ?? null,
          source_of_call: "OZG-MANAGER",
          remember_minutes: 7200,
        };

        if (!payload.community_id) {
          throw new Error(
            "community_id fehlt in window.currentUser/window.communityData"
          );
        }

        if (!payload.community_number) {
          throw new Error(
            "community_number fehlt in window.communityData.ars"
          );
        }

        if (!payload.email) {
          throw new Error(
            "email fehlt in window.currentUser.emailAddress"
          );
        }

        const response = await fetch(apiEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Token request failed: ${errText}`);
        }

        const data = await response.json();
        const token = data?.data?.token || data?.token;

        if (!token) {
          throw new Error("Token not found in response");
        }

        const newTab = window.open(targetUrl, "_blank");

        if (!newTab) {
          throw new Error(
            "Das Öffnen des neuen Tabs wird blockiert. Bitte aktivieren Sie Pop-ups."
          );
        }

        setTimeout(() => {
          try {
            newTab.postMessage({ token }, new URL(targetUrl).origin);
          } catch (postMessageError) {
            console.error(
              "[ConnectToIconManager] postMessage failed:",
              postMessageError
            );
          }
        }, 500);
      } catch (err) {
        errorDiv.innerText = `Error: ${err.message}`;
        console.error("[ConnectToIconManager] Fetch error:", err);
      } finally {
        button.disabled = false;
        button.innerText = originalText;
      }
    });

    return result;
  }

  destroy() {
    super.destroy();
    this.attachedOnce = false;
  }
}